import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { SubscriptionModel } from '@models/subscription'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/subscription/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/subscription/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/subscription/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/subscription/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/subscription/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/subscription/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/subscription/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/subscription/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/subscription/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/subscription/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/subscription/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/subscription/DELETE_FAILURE'

export type SubscriptionModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

const initialState: SubscriptionModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadSubscriptions(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<SubscriptionModel[]>> => {
    const loadedForKeys = [...getState().subscriptions.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/subscriptions', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadSubscription(subscriptionId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<SubscriptionModel>> => {
    const state = getState()
    const loadedIds = [...state.subscriptions.loadedIds]

    if (subscriptionId && !loadedIds.includes(subscriptionId)){
      loadedIds.push(subscriptionId)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/subscriptions/${subscriptionId}`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))
        return { success: false, errors }
      })

    return promise
  }
}

export function createSubscription(subscription: SubscriptionModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(subscription),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<SubscriptionModel>> => {
    dispatch(createRequest())

    const promise = api('/subscriptions', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateSubscription(subscription: SubscriptionModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(subscription),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<SubscriptionModel>> => {
    dispatch(updateRequest())

    const promise = api(`/subscriptions/${subscription.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteSubscription(subscription: SubscriptionModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch): Promise<ActionResult<SubscriptionModel>> => {
    dispatch(deleteRequest())

    const promise = api(`/subscriptions/${subscription.id}`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: SubscriptionModuleState = initialState,
  action: Action = { type: '' },
): SubscriptionModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
